import { useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  Card,
  Dialog,
  DialogContent,
  Grid,
  Stack,
  Typography,
  DialogTitle,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ApiFetch } from '../../Utils/ApiFetch';
import useGlobalContext from '../../Hooks/useGlobalContext';
import Loading from '../../Components/Generic/Loading';
import ModalConfirmOrDecline from '../../Components/Feature/ModalConfirmOrDecline';
import CompanyInfo from './components/CompanyInfo';
import FilterCompanies from './components/FilterCompanies';
import { Verified } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const CompanyValidations = () => {
  const { t } = useTranslation();
  const globalContext = useGlobalContext();
  const { selectedCompany } = globalContext;

  const [allCompanies, setAllCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [actualValidationCompanyId, setActualValidationCompanyId] =
    useState(false);
  const [actualCompanyInfo, setActualCompanyInfo] = useState(null);
  const [openDialogViewCompany, setOpenDialogViewCompany] = useState(false);

  const getAllCompaniesDelegated = () => {
    setIsLoading(true);
    ApiFetch(
      `validations/${selectedCompany.id}/company`,
      globalContext,
      true,
      async (result) => {
        if (result.status === 200) {
          const res = await result.json();
          Array.isArray(res) && setAllCompanies(res);
        }
        setIsLoading(false);
      },
      (error) => {
        console.error('Error fetching company validations:', error);
        setIsLoading(false);
      },
      {
        method: 'GET',
      }
    );
  };

  useEffect(() => {
    getAllCompaniesDelegated();
  }, [selectedCompany.id, globalContext]);

  const handleSubmitValidation = (confirm, companyId) => {
    if (confirm) setIsLoading(true);
    ApiFetch(
      `validations/${selectedCompany.id}`,
      globalContext,
      true,
      async (result) => {
        console.log(result);
        if (result.status === 200) getAllCompaniesDelegated();
        setIsLoading(false);
      },
      (error) => {
        console.error('Error updating company validations:', error);
        setIsLoading(false);
      },
      {
        method: 'PUT',
        body: JSON.stringify({
          entityId: companyId,
          entityType: 'company',
        }),
      }
    );
    setActualValidationCompanyId(false);
  };

  const handleViewCompany = (companyId) => {
    setActualCompanyInfo(companyId);
    setOpenDialogViewCompany(true);
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <FilterCompanies
          allCompanies={allCompanies}
          onFilteredCompaniesChange={setFilteredCompanies}
        />
        {filteredCompanies.map((data) => (
          <Grid item xs={12} key={data.id}>
            <Card>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ p: 1 }}>
                <Avatar aria-label="recipe" sx={{ width: 36, height: 36 }}>
                  {data.name && data.name[0].toUpperCase()}
                </Avatar>

                <Typography noWrap variant="subtitle1">
                  {data.name}
                </Typography>
                {data.validated_at && (
                  <Verified color="primary" sx={{ width: 20, height: 20 }} />
                )}
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ flexGrow: 1, justifyContent: 'flex-end' }}>
                  {!data.validated_at && (
                    <Button
                      color="primary"
                      variant="contained"
                      size="small"
                      onClick={() => setActualValidationCompanyId(data.id)}>
                      {t('validations.common.validate')}
                    </Button>
                  )}
                  <Button
                    color="primary"
                    variant="contained"
                    size="small"
                    onClick={() => handleViewCompany(data.id)}>
                    {t('validations.common.viewInfo')}
                  </Button>
                </Stack>
              </Stack>
            </Card>
          </Grid>
        ))}
        {!allCompanies || allCompanies.length === 0 ? (
          <Typography variant="body1" sx={{ p: 2 }}>
            {t('validations.companies.empty')}
          </Typography>
        ) : (
          (!filteredCompanies || filteredCompanies.length === 0) && (
            <Typography variant="body1" sx={{ p: 2 }}>
              {t('validations.common.filter.empty')}
            </Typography>
          )
        )}
      </Grid>
      <ModalConfirmOrDecline
        open={!!actualValidationCompanyId}
        modalText={t('validations.companies.confirmValidation')}
        toggleModal={setActualValidationCompanyId}
        handleAction={handleSubmitValidation}
        data={actualValidationCompanyId}
        isLoading={isLoading}
      />
      <Dialog
        open={openDialogViewCompany}
        onClose={() => setOpenDialogViewCompany(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '80vh',
            maxHeight: '80vh',
          },
        }}>
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center">
            {t('validations.companies.info')}
            <IconButton onClick={() => setOpenDialogViewCompany(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <CompanyInfo
            companyId={actualCompanyInfo}
            closeDialog={() => setOpenDialogViewCompany(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CompanyValidations;
